import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { SimpleError } from '@simonbackx/simple-errors';
import { v4 as uuidv4 } from 'uuid';
import { Formatter } from '@stamhoofd/utility';
import { upgradePriceFrom2To4DecimalPlaces } from '../upgradePriceFrom2To4DecimalPlaces.js';
import { STPackageBundle, STPackageBundleHelper } from './STPackageBundle.js';
export var STPackageType;
(function (STPackageType) {
    // Members without activities (not available in frontend anymore)
    STPackageType["LegacyMembers"] = "LegacyMembers";
    // Full members package
    STPackageType["Members"] = "Members";
    // Webshop package (max 10 webshops)
    STPackageType["Webshops"] = "Webshops";
    // One webshop package (max 1 webshop)
    STPackageType["SingleWebshop"] = "SingleWebshop";
    STPackageType["TrialMembers"] = "TrialMembers";
    STPackageType["TrialWebshops"] = "TrialWebshops";
})(STPackageType || (STPackageType = {}));
export class STPackageTypeHelper {
    static getName(type) {
        switch (type) {
            case STPackageType.LegacyMembers: return $t(`%oF`);
            case STPackageType.Members: {
                return $t(`%Om`);
            }
            case STPackageType.Webshops: {
                return $t(`%1Hk`);
            }
            case STPackageType.SingleWebshop: {
                return $t(`%oG`);
            }
            case STPackageType.TrialMembers: return $t(`%1Hl`);
            case STPackageType.TrialWebshops: return $t(`%1Hm`);
        }
    }
    /**
     * Used for renewal. Should this package renew as what bundle?
     */
    static getBundle(type) {
        switch (type) {
            case STPackageType.LegacyMembers:
            case STPackageType.Members:
                return STPackageBundle.Members;
            case STPackageType.Webshops:
            case STPackageType.SingleWebshop:
                return STPackageBundle.Webshops;
            case STPackageType.TrialMembers:
                return STPackageBundle.TrialMembers;
            case STPackageType.TrialWebshops:
                return STPackageBundle.TrialWebshops;
        }
    }
    static getTrial(type) {
        switch (type) {
            case STPackageType.LegacyMembers:
            case STPackageType.Members:
                return STPackageType.TrialMembers;
            case STPackageType.Webshops:
            case STPackageType.SingleWebshop:
                return STPackageType.TrialWebshops;
        }
        return null;
    }
}
export var STPricingType;
(function (STPricingType) {
    STPricingType["Fixed"] = "Fixed";
    /**
     * Package is renewable per year
     * Package can get extended initially to a specific date
     */
    STPricingType["PerYear"] = "PerYear";
    /**
     * Price is per member, per year
     */
    STPricingType["PerMember"] = "PerMember";
})(STPricingType || (STPricingType = {}));
export function getPricingTypeName(type) {
    switch (type) {
        case STPricingType.Fixed: return $t('%1Xm');
        case STPricingType.PerYear: return $t('%1aT');
        case STPricingType.PerMember: return $t('%1VT');
    }
}
export function getPricingTypeSuffix(type) {
    switch (type) {
        case STPricingType.Fixed: return $t('%1W3');
        case STPricingType.PerYear: return $t('%1ae');
        case STPricingType.PerMember: return $t('%1W1');
    }
}
export class STPackageMeta extends AutoEncoder {
    get name() {
        return STPackageTypeHelper.getName(this.type);
    }
    get isTrial() {
        return this.type.includes('Trial');
    }
    get isWebshops() {
        return this.type === STPackageType.Webshops || this.type === STPackageType.SingleWebshop || this.type === STPackageType.TrialWebshops;
    }
    /**
     * When service fees or per member pricing is set, a mandate is required
     */
    get requiresMandate() {
        return this.serviceFeeFixed !== 0 || this.serviceFeePercentage !== 0 || !!this.serviceFeeMinimum || (!!this.unitPrice && this.pricingType === STPricingType.PerMember);
    }
    /**
     * @deprecated
     * Not really trustworthy as it has not always been set correctly in the past
     * and won't be set any longer
     */
    didRenewId;
    type;
    pricingType = STPricingType.Fixed;
    keepPricesOnRenewal;
    /**
     * One time price for the package, per year, or per member depending on pricingType
     */
    unitPrice = 0;
    /**
     * Service fees, percentage. 100_00 = 100%, 1 = 0.01%
     */
    serviceFeePercentage = 0;
    /**
     * Fixed service fee per payment, in cents
     */
    serviceFeeFixed = 0;
    /**
     * Fixed service fee per payment, in cents
     */
    serviceFeeMinimum = null;
    /**
     * Fixed service fee per payment, in cents
     */
    serviceFeeMaximum = null;
    /**
     * @deprecated
     * Contains the (paid) invoiced amount.
     *
     * This should be replaced by manually querying the balance items created for the package
     */
    paidAmount = 0;
    /**
     * @deprecated
     * Contains the (paid) invoiced price. Used for statistics and reporting
     * This should be replaced by manually querying the balance items created for the package
     */
    paidPrice = 0;
    /**
     * Minimum amount that will needs to get invoiced. Only used for first invoice
     */
    minimumAmount = 1;
    /**
     * Only used if applicable
     */
    autorenew = true;
    /**
     * Only used if applicable
     */
    allowRenew = false;
    /// If a top up is done, and the payment fails, set the date here
    /// + increase the payment failed count.
    /// After x days this will disable all functions if it keeps failing
    firstFailedPayment = null;
    paymentFailedCount = 0;
    /**
     * Date when the package starts. Is is valid until validUntil
     */
    startDate;
    canDeactivate = false;
    get humanPricing() {
        const strs = [];
        if (this.unitPrice) {
            if (this.pricingType === STPricingType.PerMember) {
                strs.push($t(`%1T2`, { price: Formatter.price(this.unitPrice) }));
            }
            else if (this.pricingType === STPricingType.PerYear) {
                strs.push($t(`%1RJ`, { price: Formatter.price(this.unitPrice) }));
            }
            else if (this.pricingType === STPricingType.Fixed) {
                strs.push($t(`%1RX`, { amount: Formatter.integer(this.minimumAmount), price: Formatter.price(this.unitPrice) }));
            }
            if (this.minimumAmount) {
                if (this.pricingType !== STPricingType.Fixed) {
                    strs.push($t(`%1Ta`, { amount: Formatter.integer(this.minimumAmount) }));
                }
            }
        }
        let suffix = $t('%1U3');
        if (this.type === STPackageType.Members) {
            suffix = $t('%1Ts');
        }
        let minimumSuffix = '';
        let maximumSuffix = '';
        if (this.serviceFeeMinimum) {
            minimumSuffix = $t('%1RM', { price: Formatter.price(this.serviceFeeMinimum) });
            if (this.type === STPackageType.Members) {
                minimumSuffix = $t('%1RM', { price: Formatter.price(this.serviceFeeMinimum) });
            }
        }
        if (this.serviceFeeMaximum) {
            maximumSuffix = $t('%1Tl', { price: Formatter.price(this.serviceFeeMaximum) });
            if (this.type === STPackageType.Members) {
                maximumSuffix = $t('%1Tl', { price: Formatter.price(this.serviceFeeMaximum) });
            }
        }
        if (this.serviceFeePercentage && this.serviceFeeFixed) {
            strs.push(Formatter.percentage(this.serviceFeePercentage) + ` + ` + Formatter.price(this.serviceFeeFixed) + ' ' + suffix + (minimumSuffix ? (', ' + minimumSuffix) : '') + (maximumSuffix ? (', ' + maximumSuffix) : ''));
        }
        else if (this.serviceFeeFixed) {
            strs.push((Formatter.price(this.serviceFeeFixed) + ' ' + suffix + (minimumSuffix ? (', ' + minimumSuffix) : '') + (maximumSuffix ? (', ' + maximumSuffix) : '')));
        }
        else if (this.serviceFeePercentage) {
            strs.push(Formatter.percentage(this.serviceFeePercentage) + (minimumSuffix ? (' ' + minimumSuffix) : '') + (maximumSuffix ? ((minimumSuffix ? ', ' : ' ') + maximumSuffix) : ''));
        }
        if (strs.length === 0) {
            strs.push($t(`%1Mn`));
        }
        return strs.join('\n');
    }
}
__decorate([
    field({ decoder: StringDecoder, optional: true })
], STPackageMeta.prototype, "didRenewId", void 0);
__decorate([
    field({ decoder: new EnumDecoder(STPackageType) })
], STPackageMeta.prototype, "type", void 0);
__decorate([
    field({ decoder: new EnumDecoder(STPricingType) })
], STPackageMeta.prototype, "pricingType", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 398, defaultValue: () => false })
], STPackageMeta.prototype, "keepPricesOnRenewal", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], STPackageMeta.prototype, "unitPrice", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true })
], STPackageMeta.prototype, "serviceFeePercentage", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true })
], STPackageMeta.prototype, "serviceFeeFixed", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true, nullable: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true, nullable: true })
], STPackageMeta.prototype, "serviceFeeMinimum", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true, nullable: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true, nullable: true })
], STPackageMeta.prototype, "serviceFeeMaximum", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], STPackageMeta.prototype, "paidAmount", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], STPackageMeta.prototype, "paidPrice", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], STPackageMeta.prototype, "minimumAmount", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], STPackageMeta.prototype, "autorenew", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], STPackageMeta.prototype, "allowRenew", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackageMeta.prototype, "firstFailedPayment", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], STPackageMeta.prototype, "paymentFailedCount", void 0);
__decorate([
    field({ decoder: DateDecoder })
], STPackageMeta.prototype, "startDate", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], STPackageMeta.prototype, "canDeactivate", void 0);
/**
 * A package contains a price agreement for a given period that might be renewable
 */
export class STPackage extends AutoEncoder {
    id;
    meta;
    createdAt = new Date();
    updatedAt = new Date();
    /**
     * validAt is null if the initial needed payment is missing. The package can be ignore
     * It contains the date when the package was validated and added correctly.
     */
    validAt = null;
    validUntil = null;
    /// Disable / delete this package after this date (also no renew allowed). Can't keep using the currently saved pricing
    removeAt = null;
    /**
     * Helper to handle edge cases where validUntil is null but removeAt is set
     */
    get endDate() {
        if (!this.removeAt) {
            return this.validUntil;
        }
        if (!this.validUntil) {
            return this.removeAt;
        }
        return new Date(Math.min(this.validUntil.getTime(), this.removeAt.getTime()));
    }
    get isActive() {
        return !!this.validAt && (!this.endDate || this.endDate > new Date());
    }
    /**
     * Package as if it has been renewed
     */
    createRenewed() {
        if (!this.meta.allowRenew) {
            throw new SimpleError({
                code: 'not_allowed',
                message: 'Not allowed',
                human: $t(`%x7`),
            });
        }
        if (!this.meta.keepPricesOnRenewal) {
            return STPackageBundleHelper.getCurrentPackage(STPackageTypeHelper.getBundle(this.meta.type), new Date(Math.max(new Date().getTime(), this.validUntil?.getTime() ?? 0)));
        }
        const pack = this.clone();
        pack.id = uuidv4();
        // Not yet valid / active (ignored until valid)
        pack.validAt = null;
        const start = Formatter.luxon(new Date(Math.max(new Date().getTime(), this.endDate?.getTime() ?? 0) + 1_000)).startOf('day');
        const validUntil = start.plus({ years: 1 }).endOf('day');
        const removeAt = validUntil.plus({ months: 1 }).endOf('day');
        pack.meta.paidAmount = 0;
        pack.meta.paidPrice = 0;
        pack.meta.firstFailedPayment = null;
        pack.meta.didRenewId = this.id;
        // Duration for renewals is always a year ATM
        pack.meta.startDate = start.toJSDate();
        pack.validUntil = validUntil.toJSDate();
        // Remove (= not renewable) if not renewed after 3 months
        pack.removeAt = removeAt.toJSDate();
        if (this.meta.type === STPackageType.SingleWebshop) {
            // Deprecated package
            pack.meta.type = STPackageType.Webshops;
        }
        return pack;
    }
    get status() {
        return STPackageStatus.create({
            startDate: this.meta.startDate,
            validUntil: this.validUntil,
            removeAt: this.removeAt,
            firstFailedPayment: this.meta.firstFailedPayment,
        });
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], STPackage.prototype, "id", void 0);
__decorate([
    field({ decoder: STPackageMeta })
], STPackage.prototype, "meta", void 0);
__decorate([
    field({ decoder: DateDecoder })
], STPackage.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], STPackage.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackage.prototype, "validAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackage.prototype, "validUntil", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackage.prototype, "removeAt", void 0);
export class STPackageStatusServiceFee extends AutoEncoder {
    fixed = 0;
    percentage = 0;
    minimum = null;
    maximum = null;
    startDate;
    endDate = null;
}
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], STPackageStatusServiceFee.prototype, "fixed", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], STPackageStatusServiceFee.prototype, "percentage", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true, optional: true })
], STPackageStatusServiceFee.prototype, "minimum", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true, optional: true })
], STPackageStatusServiceFee.prototype, "maximum", void 0);
__decorate([
    field({ decoder: DateDecoder })
], STPackageStatusServiceFee.prototype, "startDate", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackageStatusServiceFee.prototype, "endDate", void 0);
export class STPackageStatus extends AutoEncoder {
    startDate;
    validUntil = null;
    /// Disable / delete this package after this date (also no renew allowed). Can't keep using the currently saved pricing
    removeAt = null;
    firstFailedPayment = null;
    /**
     * Service fees, percentage. 100_00 = 100%, 1 = 0.01%
     */
    serviceFees = [];
    get activeServiceFees() {
        const now = new Date();
        const filtered = this.serviceFees.filter((fee) => {
            if (fee.startDate <= now && (fee.endDate === null || fee.endDate >= now)) {
                return true;
            }
            return false;
        });
        // Return maximum fees
        if (filtered.length === 0) {
            return STPackageStatusServiceFee.create({
                fixed: 0,
                percentage: 0,
                startDate: new Date(),
                endDate: null,
            });
        }
        if (filtered.length === 1) {
            return filtered[0]; // Only one fee, return it
        }
        console.warn('Multiple active service fees found, merging them', filtered);
        return filtered.reduce((max, current) => {
            return STPackageStatusServiceFee.create({
                fixed: Math.max(max.fixed, current.fixed),
                percentage: Math.max(max.percentage, current.percentage),
                startDate: max.startDate,
                endDate: max.endDate,
                minimum: max.minimum !== null ? Math.max(max.minimum, current.minimum ?? 0) : current.minimum,
                maximum: max.maximum !== null ? Math.max(max.maximum, current.maximum ?? 0) : current.maximum,
            });
        });
    }
    get isPaymentExpired() {
        const expire = new Date();
        expire.setDate(expire.getDate() - 28);
        if (this.firstFailedPayment && this.firstFailedPayment < expire) {
            return true;
        }
        return false;
    }
    get isActive() {
        const d = new Date();
        /// Active if it starts within 10 seconds (fixes time differences between server and clients)
        if (this.startDate && this.startDate > new Date(d.getTime() + 10 * 1000)) {
            return false;
        }
        if (this.removeAt && this.removeAt < d) {
            return false;
        }
        if (this.validUntil && this.validUntil < d) {
            return false;
        }
        if (this.isPaymentExpired) {
            return false;
        }
        return true;
    }
    /**
     * Purchased the package, the package is not yet removed, but it is expired or not paid after 4 weeks following a failed payment
     */
    get wasActive() {
        if (this.isActive) {
            return false;
        }
        const d = new Date();
        // If only valid for less than 30 days, still allow to use the trial package - don't count the package as been active
        if (this.validUntil && this.validUntil.getTime() - this.startDate.getTime() < 60 * 1000 * 60 * 24 * 31) {
            return false;
        }
        /**
         * If the removeAt date is past, also disable wasActive again
         */
        if (this.removeAt && this.removeAt < d) {
            // Passed!
            return false;
        }
        if (this.validUntil && this.validUntil < d) {
            // Passed!
            return true;
        }
        // Deactivate module if payment failed, and not reactivated after 4 weeks
        if (this.isPaymentExpired) {
            // did not pay!
            return true;
        }
        return false;
    }
    get deactivateDate() {
        const dates = [];
        if (this.removeAt !== null) {
            dates.push(this.removeAt);
        }
        if (this.validUntil !== null) {
            dates.push(this.validUntil);
        }
        if (this.firstFailedPayment !== null) {
            const expire = new Date(this.firstFailedPayment);
            expire.setDate(expire.getDate() + 28);
            dates.push(expire);
        }
        if (dates.length === 0) {
            return null;
        }
        return new Date(Math.min(...dates.map(d => d.getTime())));
    }
    merge(status) {
        if (status.startDate < this.startDate) {
            this.startDate = status.startDate;
            // We don't support gaps in packages yet. So this is ignored here
        }
        if (status.validUntil === null) {
            this.validUntil = null;
        }
        else if (this.validUntil !== null) {
            if (status.validUntil > this.validUntil) {
                this.validUntil = status.validUntil;
            }
        }
        if (status.removeAt === null) {
            this.removeAt = null;
        }
        else if (this.removeAt !== null) {
            if (status.removeAt > this.removeAt) {
                this.removeAt = status.removeAt;
            }
        }
        if (this.firstFailedPayment === null) {
            this.firstFailedPayment = status.firstFailedPayment;
        }
        else if (status.firstFailedPayment !== null) {
            if (status.firstFailedPayment < this.firstFailedPayment) {
                this.firstFailedPayment = status.firstFailedPayment;
            }
        }
        this.serviceFees.push(...status.serviceFees);
    }
}
__decorate([
    field({ decoder: DateDecoder })
], STPackageStatus.prototype, "startDate", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackageStatus.prototype, "validUntil", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackageStatus.prototype, "removeAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackageStatus.prototype, "firstFailedPayment", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(STPackageStatusServiceFee), optional: true })
], STPackageStatus.prototype, "serviceFees", void 0);
//# sourceMappingURL=STPackage.js.map